import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, post } from './api';

// Minimum length for new passwords (change, reset, invitation).
export const MIN_PASSWORD = 12;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const me = await api('/auth/me');
      setUser(me);
      setLoading(false);
      return me;
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    const onUnauth = () => {
      if (location.pathname !== '/login') {
        localStorage.removeItem('token');
        setUser(null);
        navigate('/login', { replace: true, state: { from: location.pathname } });
      }
    };
    window.addEventListener('auth:unauthorized', onUnauth);
    return () => window.removeEventListener('auth:unauthorized', onUnauth);
  }, [navigate, location]);

  const login = useCallback(
    async (email, password) => {
      // OAuth2PasswordRequestForm expects form-encoded
      const body = new URLSearchParams();
      body.set('username', email);
      body.set('password', password);
      const res = await api('/auth/login', {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (res?.mfa_required) {
        return { mfaToken: res.mfa_token };
      }
      localStorage.setItem('token', res.access_token);
      if (res.refresh_token) localStorage.setItem('refresh_token', res.refresh_token);
      await refreshMe();
      return { ok: true };
    },
    [refreshMe],
  );

  const verifyMfa = useCallback(
    async (mfaToken, code) => {
      const res = await post('/auth/login/mfa', { mfa_token: mfaToken, code });
      localStorage.setItem('token', res.access_token);
      if (res.refresh_token) localStorage.setItem('refresh_token', res.refresh_token);
      await refreshMe();
    },
    [refreshMe],
  );

  // Revoke the tokens server-side first, then forget them locally. A failed
  // revoke (network, already expired) must never block signing out.
  const logout = useCallback(async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (token) {
      try {
        await post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : undefined);
      } catch {
        /* ignore: local sign-out happens regardless */
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
